import { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Building2, Image as ImageIcon, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const fields = [
  { key: "company_name", label: "Nombre de la empresa", ph: "JDMTECH" },
  { key: "contact_name", label: "Persona de contacto", ph: "Jesús Domínguez Martínez" },
  { key: "slogan", label: "Eslogan", ph: "IT SOLUTIONS" },
  { key: "services", label: "Servicios (subtítulo)", ph: "Redes · CCTV · Servicios Informáticos" },
  { key: "phone", label: "Teléfono", ph: "624501153" },
  { key: "email", label: "Email", ph: "soporte@jdmtech.es" },
  { key: "nif", label: "NIF / CIF", ph: "Opcional" },
  { key: "address", label: "Dirección", ph: "Lebrija, Sevilla (41740)" },
];

export default function Settings() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [nextNumber, setNextNumber] = useState("");
  const [savingNum, setSavingNum] = useState(false);
  const fileRef = useRef();

  const loadLogo = useCallback(async () => {
    try {
      const res = await api.get("/settings/logo", { responseType: "blob" });
      setLogoUrl(URL.createObjectURL(res.data));
    } catch (e) { setLogoUrl(null); }
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/settings");
      setData(res.data);
      if (res.data.logo_path) loadLogo();
    } finally {
      setLoading(false);
    }
    try {
      const n = await api.get("/quotes/next-number");
      setNextNumber(String(n.data.next_number));
    } catch (e) { console.error("Next number load error:", e); }
  }, [loadLogo]);

  useEffect(() => { load(); }, [load]);

  const saveNumber = async () => {
    const v = parseInt(nextNumber, 10);
    if (!v || v < 1) { toast.error("Introduce un número válido"); return; }
    setSavingNum(true);
    try {
      await api.put("/quotes/next-number", null, { params: { value: v } });
      toast.success("Numeración actualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingNum(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((f) => { payload[f.key] = data[f.key] || ""; });
      payload.disclaimer = data.disclaimer || "";
      payload.thanks = data.thanks || "";
      await api.put("/settings", payload);
      toast.success("Ajustes guardados");
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo actualizado");
      loadLogo();
    } catch (e) {
      toast.error("No se pudo subir el logo");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Ajustes</h1>
        <p className="text-muted-foreground mt-1">Datos que aparecen en tus presupuestos PDF</p>
      </div>

      {/* Logo */}
      <div className="rounded-md border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon className="h-5 w-5 text-primary" />
          <h2 className="font-display font-semibold">Logo</h2>
        </div>
        <div className="flex items-center gap-5">
          <div className="h-20 w-20 rounded-md border border-border bg-muted flex items-center justify-center overflow-hidden shrink-0">
            {logoUrl ? <img src={logoUrl} alt="Logo" className="h-full w-full object-contain" data-testid="logo-preview" />
                     : <Building2 className="h-8 w-8 text-muted-foreground" />}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="logo-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-logo-button">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
              Subir logo
            </Button>
            <p className="text-xs text-muted-foreground mt-2">PNG o JPG. Se usará en la cabecera del PDF.</p>
          </div>
        </div>
      </div>

      {/* Company data */}
      <div className="rounded-md border border-border bg-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Hash className="h-5 w-5 text-primary" />
          <h2 className="font-display font-semibold">Numeración de presupuestos</h2>
        </div>
        <p className="text-sm text-muted-foreground -mt-2">
          Fija el número que llevará tu próximo presupuesto para continuar tu serie actual.
        </p>
        <div className="flex items-end gap-3">
          <div className="w-40">
            <Label>Próximo número</Label>
            <Input type="number" inputMode="numeric" value={nextNumber}
              onChange={(e) => setNextNumber(e.target.value)} data-testid="settings-next-number" />
          </div>
          <Button variant="outline" onClick={saveNumber} disabled={savingNum} data-testid="save-number-button">
            {savingNum && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Aplicar
          </Button>
        </div>
      </div>

      {/* Company data */}
      <div className="rounded-md border border-border bg-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          <h2 className="font-display font-semibold">Datos de empresa</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.key} className={f.key === "services" || f.key === "address" ? "sm:col-span-2" : ""}>
              <Label>{f.label}</Label>
              <Input value={data[f.key] || ""} placeholder={f.ph}
                onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                data-testid={`settings-${f.key}`} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <Label>Texto de agradecimiento (pie)</Label>
            <Input value={data.thanks || ""} placeholder="Gracias por su confianza."
              onChange={(e) => setData({ ...data, thanks: e.target.value })} data-testid="settings-thanks" />
          </div>
          <div className="sm:col-span-2">
            <Label>Aviso legal / condiciones</Label>
            <Textarea rows={3} value={data.disclaimer || ""} placeholder="Los precios indicados..."
              onChange={(e) => setData({ ...data, disclaimer: e.target.value })} data-testid="settings-disclaimer" />
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-settings-button">
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Guardar cambios
        </Button>
      </div>
    </div>
  );
}
